// ─────────────────────────────────────────────────────────────────────────────
// Couche CRM — vue « 3 niveaux » (Compte > Entité > Équipe) sur le stockage
// partagé du Cockpit, qui en compte davantage :
//
//   db.clients[]            → Compte      (grand compte)
//     .clients[]            → Entité
//       .entities[]         → sous-entité (conteneur, invisible côté CRM)
//         .subs[]           → Équipe      (+ status, + domains)
//
// La sous-entité reste dans les données pour ne rien casser du Cockpit
// (positionnements, matching, recherche) : le CRM la traverse sans l'exposer.
// Toute équipe créée depuis le CRM atterrit dans la sous-entité SE_DEFAUT.
// ─────────────────────────────────────────────────────────────────────────────

export const SE_DEFAUT = 'Général';

// Statuts d'une équipe dans le périmètre d'un compte.
export const TEAM_STATUS = {
  actif: { label: 'Active', color: '#4ADE80', hint: 'missions en cours' },
  encours: { label: 'En cours', color: '#60A5FA', hint: 'discussions engagées' },
  cible: { label: 'Cible', color: '#FACC15', hint: 'à développer' },
  non: { label: 'Non adressée', color: '#9d9bc0', hint: 'potentiel' },
};
export const STATUS_ORDER = ['actif', 'encours', 'cible', 'non'];
export const statusOf = (eq) => (eq && TEAM_STATUS[eq.status] ? eq.status : 'non');

export const TYPES_ACTION = ['Appel', 'Mail', 'RDV', 'Note'];
export const RELANCE_DAYS = 30;

export const initials = (s) =>
  (s || '?').trim().split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase();
export const fullName = (f) => [f?.prenom, f?.nom].filter(Boolean).join(' ').trim() || 'Sans nom';

// ── Lecture ─────────────────────────────────────────────────────────────────

export const comptes = (db) => db?.clients || [];
export const compteOf = (db, name) => comptes(db).find((c) => c.name === name) || null;
export const entitesOf = (compte) => (compte && compte.clients) || [];
export const entiteOf = (compte, en) => entitesOf(compte).find((e) => e.name === en) || null;

// Équipes d'une entité, toutes sous-entités confondues (aplaties pour le CRM).
export const equipesOf = (entite) =>
  ((entite && entite.entities) || []).flatMap((se) =>
    (se.subs || []).map((eq) => ({ ...eq, _se: se.name }))
  );

// Toutes les équipes d'un compte, avec leur entité de rattachement.
export const equipesOfCompte = (compte) =>
  entitesOf(compte).flatMap((e) => equipesOf(e).map((eq) => ({ entite: e.name, eq })));

// ── Fiches clients ──────────────────────────────────────────────────────────

export const fichesOf = (db, account) => (db?.fiches || []).filter((f) => f.account === account);
export const fichesOfTeam = (db, account, entity, team) =>
  (db?.fiches || []).filter((f) => f.account === account && f.entity === entity && f.team === team);
export const ficheOf = (db, id) => (db?.fiches || []).find((f) => f.id === id) || null;

export const emptyFiche = (account, entity, team) => ({
  prenom: '', nom: '', email: '', phone: '', fonction: '',
  account, entity, team,
  activite: '', hobbies: [], linkedin: '', anniversaire: '',
  decisionnaire: false, missions: [],
});

// ── Actions (appel / mail / RDV / note) ─────────────────────────────────────

export const actionsOf = (db, ficheId) =>
  (db?.actions || [])
    .filter((a) => a.ficheId === ficheId)
    .sort((a, b) => String(b.date).localeCompare(String(a.date)));

export const lastActionOf = (db, ficheId) => actionsOf(db, ficheId)[0] || null;

export const daysSince = (date) => {
  if (!date) return null;
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return null;
  return Math.floor((Date.now() - d.getTime()) / 86400000);
};

// Jours depuis le dernier contact — null si jamais contacté.
export const daysSinceContact = (db, ficheId) => {
  const a = lastActionOf(db, ficheId);
  return a ? daysSince(a.date) : null;
};

// Fiches à relancer : jamais contactées, ou sans contact depuis RELANCE_DAYS.
export const toRelance = (db, seuil = RELANCE_DAYS) =>
  (db?.fiches || []).filter((f) => {
    const d = daysSinceContact(db, f.id);
    return d === null || d >= seuil;
  });

// ── Anniversaires ───────────────────────────────────────────────────────────

// Jours avant le prochain anniversaire (0 = aujourd'hui), null si non renseigné.
export const daysToBirthday = (anniv) => {
  if (!anniv) return null;
  const d = new Date(anniv);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  let next = new Date(today.getFullYear(), d.getMonth(), d.getDate());
  if (next < today) next = new Date(today.getFullYear() + 1, d.getMonth(), d.getDate());
  return Math.round((next - today) / 86400000);
};

// ── Écriture (à passer à update() du contexte) ──────────────────────────────

const compteIn = (n, account) => (n.clients || []).find((c) => c.name === account);

export function addEntite(n, account, name) {
  const c = compteIn(n, account);
  if (!c || !name) return;
  c.clients = c.clients || [];
  if (c.clients.find((e) => e.name === name)) return;
  c.clients.push({ name, contact: '', email: '', entities: [] });
}

export function renameEntite(n, account, from, to) {
  const c = compteIn(n, account);
  const e = c && (c.clients || []).find((x) => x.name === from);
  if (!e || !to || from === to) return;
  if ((c.clients || []).find((x) => x.name === to)) return;
  e.name = to;
  (n.fiches || []).forEach((f) => {
    if (f.account === account && f.entity === from) f.entity = to;
  });
}

export function removeEntite(n, account, name) {
  const c = compteIn(n, account);
  if (!c) return;
  c.clients = (c.clients || []).filter((e) => e.name !== name);
  n.fiches = (n.fiches || []).filter((f) => !(f.account === account && f.entity === name));
}

export function addEquipe(n, account, entity, name) {
  const c = compteIn(n, account);
  const e = c && (c.clients || []).find((x) => x.name === entity);
  if (!e || !name) return;
  e.entities = e.entities || [];
  // Une équipe déjà présente dans n'importe quelle sous-entité ne se duplique pas.
  if (e.entities.some((se) => (se.subs || []).some((s) => s.name === name))) return;
  let se = e.entities.find((x) => x.name === SE_DEFAUT) || e.entities[0];
  if (!se) { se = { name: SE_DEFAUT, subs: [] }; e.entities.push(se); }
  se.subs = se.subs || [];
  se.subs.push({ name, domains: [], status: 'non' });
}

// Retrouve l'équipe à travers la sous-entité, que le CRM n'expose pas.
const findEquipe = (n, account, entity, team) => {
  const c = compteIn(n, account);
  const e = c && (c.clients || []).find((x) => x.name === entity);
  if (!e) return null;
  for (const se of e.entities || []) {
    const eq = (se.subs || []).find((s) => s.name === team);
    if (eq) return eq;
  }
  return null;
};

export function setEquipeStatus(n, account, entity, team, status) {
  const eq = findEquipe(n, account, entity, team);
  if (eq) eq.status = status;
}

export function renameEquipe(n, account, entity, from, to) {
  const eq = findEquipe(n, account, entity, from);
  if (!eq || !to || from === to) return;
  if (findEquipe(n, account, entity, to)) return;
  eq.name = to;
  (n.fiches || []).forEach((f) => {
    if (f.account === account && f.entity === entity && f.team === from) f.team = to;
  });
}

export function removeEquipe(n, account, entity, team) {
  const c = compteIn(n, account);
  const e = c && (c.clients || []).find((x) => x.name === entity);
  if (!e) return;
  (e.entities || []).forEach((se) => { se.subs = (se.subs || []).filter((s) => s.name !== team); });
  n.fiches = (n.fiches || []).filter(
    (f) => !(f.account === account && f.entity === entity && f.team === team)
  );
}

export function saveFiche(n, fiche) {
  n.fiches = n.fiches || [];
  if (fiche.id) {
    const i = n.fiches.findIndex((f) => f.id === fiche.id);
    if (i >= 0) n.fiches[i] = { ...n.fiches[i], ...fiche };
    return fiche.id;
  }
  const id = n.nextFid || 1;
  n.nextFid = id + 1;
  n.fiches.push({ ...fiche, id });
  return id;
}

export function removeFiche(n, id) {
  n.fiches = (n.fiches || []).filter((f) => f.id !== id);
  n.actions = (n.actions || []).filter((a) => a.ficheId !== id);
}

export function addAction(n, ficheId, { type, date, note }) {
  n.actions = n.actions || [];
  const id = n.nextAid || 1;
  n.nextAid = id + 1;
  n.actions.push({ id, ficheId, type: type || 'Note', date: date || new Date().toISOString().slice(0, 10), note: note || '' });
  return id;
}

export function removeAction(n, id) {
  n.actions = (n.actions || []).filter((a) => a.id !== id);
}
